import axios, { AxiosInstance, AxiosResponse } from 'axios';

// Dynamically determine the API Base URL.
// If VITE_API_BASE_URL is set as an absolute URL (starts with http), use it.
// If we are running in the browser and on a production domain (not localhost/127.0.0.1),
// dynamically use the current page's origin + '/api'.
// Otherwise, fallback to VITE_API_BASE_URL or local development URL http://localhost:8000/api.
const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (envUrl && envUrl.startsWith('http')) {
    return envUrl;
  }
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `${window.location.origin}/api`;
    }
  }
  return envUrl || 'http://localhost:8000/api';
};

const API_BASE_URL = getApiBaseUrl();

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{ resolve: Function; reject: Function }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // Increased from 10s to 30s for better reliability
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      async (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // If sending FormData, remove Content-Type to let browser set it
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle common errors and auto-refresh tokens
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;

        console.log('🔴 API Error Interceptor:', {
          status: error.response?.status,
          url: originalRequest?.url,
          pathname: window.location.pathname
        });

        // Handle 403 Forbidden (shop suspended/expired)
        if (error.response?.status === 403) {
          const errorData = error.response.data;
          const shopStatus = errorData.shop_status;
          
          console.log('🚫 403 Forbidden - Shop Status:', shopStatus);
          
          // If it's a shop access issue (not a permission issue), logout and redirect
          if (shopStatus && ['suspended', 'expired', 'trial'].includes(shopStatus)) {
            console.log('🚫 Shop inactive - logging out user');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            
            // Don't redirect on landing page - just reject the error
            if (window.location.pathname === '/') {
              return Promise.reject(error);
            }
            
            // Redirect to login with error info
            if (!window.location.pathname.includes('/login')) {
              // Store error info for login page to show modal
              sessionStorage.setItem('shop_suspension_error', JSON.stringify({
                shop_status: shopStatus,
                message: errorData.message
              }));
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          const refreshToken = localStorage.getItem('auth_token');
          
          console.log('⚠️ 401 Error - Has token:', !!refreshToken, 'Path:', window.location.pathname);
          
          // If on landing page, don't redirect - just reject
          if (window.location.pathname === '/') {
            return Promise.reject(error);
          }
          
          // If no token exists or we're already on login page, don't try to refresh
          if (!refreshToken || window.location.pathname.includes('/login')) {
            console.log('🔴 No token or on login page - clearing storage');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            return Promise.reject(error);
          }

          // Don't try to refresh for auth endpoints themselves
          if (originalRequest.url?.includes('/auth/login') || 
              originalRequest.url?.includes('/auth/refresh')) {
            console.log('🔴 Auth endpoint failed - redirecting to login');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            window.location.href = '/login';
            return Promise.reject(error);
          }

          if (this.isRefreshing) {
            // If already refreshing, queue the request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.client(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const response = await this.client.post('/auth/refresh', {}, {
              headers: { Authorization: `Bearer ${refreshToken}` }
            });

            if (response.data.success) {
              const newToken = response.data.data.token;
              localStorage.setItem('auth_token', newToken);
              
              // Process failed queue
              this.failedQueue.forEach(({ resolve }) => resolve(newToken));
              this.failedQueue = [];
              
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, logout user
            console.error('🔴 Token refresh failed:', refreshError);
            this.failedQueue.forEach(({ reject }) => reject(refreshError));
            this.failedQueue = [];
            
            console.log('🔴 Clearing auth and redirecting to login');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            
            // Force redirect to login (but not on landing page)
            if (!window.location.pathname.includes('/login') && window.location.pathname !== '/') {
              console.log('🔴 Forcing redirect to /login');
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Network errors or timeout
        if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
          console.error('Network error or timeout:', error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: any): Promise<T> {
    const response = await this.client.get(url, config);
    console.log(`📡 API GET ${url}:`, {
      status: response.status,
      headers: response.headers,
      data: response.data
    });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.post(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    console.log('DELETE request to:', url);
    try {
      const response = await this.client.delete(url);
      console.log('DELETE response:', response.status, response.data);
      return response.data;
    } catch (error: any) {
      console.log('DELETE error:', error.response?.status, error.response?.data, 'URL:', url);
      throw error;
    }
  }
}

export const apiClient = new ApiClient();